/**
 * B3 · ConsumerFlow shell — O5: Cardmoji's own prepaid surface (`/create`).
 *
 * The funnel is PROGRAM-resolved (O4 `get_public_program_by_slug`), not tenant-resolved.
 * No bank appears in the URL or in the owning context; the fulfiller is an opaque id.
 * Explicitly isolated from B2B (`TenantLoader`/`TenantProvider`), which stays on `/t/`.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";
import { GhostButton } from "@/components/consumer/chrome";
import {
  getPublicProgramBySlug,
  LAUNCH_PROGRAM_SLUG,
  PublicProgram,
} from "@/services/programs";
import {
  Attribution,
  captureAttributionFromLocation,
  saveAttribution,
  readAttribution,
} from "@/lib/consumer-session";
import S1StyleGallery from "./consumer/S1StyleGallery";
import S2Selfie from "./consumer/S2Selfie";
import S2bSameFace from "./consumer/S2bSameFace";
import S3VerifyConsent from "./consumer/S3VerifyConsent";
import S4Generating from "./consumer/S4Generating";
import S5Reveal from "./consumer/S5Reveal";
import S5bShare from "./consumer/S5bShare";
/* N1 · the details form, split into five single-purpose screens. They are views
   over ONE staged order object and ONE set of validators (@/lib/details-steps). */
import NameStep from "./consumer/details/NameStep";
import PhoneStep from "./consumer/details/PhoneStep";
import DobStep from "./consumer/details/DobStep";
import AddressStep from "./consumer/details/AddressStep";
import S6bLoad from "./consumer/S6bLoad";
import S7Pay from "./consumer/S7Pay";
import S7bAuthorise from "./consumer/S7bAuthorise";
import S8Confirmed from "./consumer/S8Confirmed";
import { recordArrival } from "@/lib/funnel-arrivals";
import { setFunnelContext } from "@/lib/funnel-signals";

/** Records reaching a screen, once per screen per visit. Renders nothing. */
function ArrivalRecorder({ slug, programId }: { slug: string; programId: string | null }) {
  const { pathname } = useLocation();
  useEffect(() => {
    // Parked for the shared back affordance and the payment element, which are
    // too far from the program to receive it as props.
    setFunnelContext(slug, programId);
    recordArrival(slug, pathname, programId);
  }, [slug, pathname, programId]);
  return null;
}



export interface ConsumerProgram extends PublicProgram {
  /** Convenience aliases used across S0–S7. */
  id: string;
  name: string;
  primary_color: string;
  logo_url: string | null;
  config: Record<string, any>;
}

interface Ctx {
  program: ConsumerProgram;
  attribution: Attribution;
}

const ConsumerCtx = createContext<Ctx | null>(null);

export function useConsumerFlow(): Ctx {
  const c = useContext(ConsumerCtx);
  if (!c) throw new Error("useConsumerFlow must be used inside <ConsumerFlow>");
  return c;
}

export default function ConsumerFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const [program, setProgram] = useState<ConsumerProgram | null>(null);
  const [error, setError] = useState<string | null>(null);
  const programSlug = LAUNCH_PROGRAM_SLUG;

  // Capture attribution on first mount (URL params → localStorage, first-touch wins).
  useEffect(() => {
    const attr = captureAttributionFromLocation(location.search);
    saveAttribution(programSlug, attr);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getPublicProgramBySlug(programSlug);
      if (cancelled) return;
      if (error || !data) {
        setError("This card programme isn't available right now.");
        return;
      }
      setProgram({
        ...data,
        id: data.program_id,
        name: data.display_name,
        primary_color: data.brand_primary_color || "#111111",
        logo_url: data.brand_logo_url,
        // B1–B4: consumer-PRODUCT config comes from the PROGRAM (sanitised projection).
        config: data.config ?? {},
      });
    })();
    return () => { cancelled = true; };
  }, [programSlug]);

  const attribution = useMemo(() => readAttribution(programSlug), [programSlug, location.search]);

  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-6 text-center" data-testid="consumer-error">
        <div>
          <h1 className="text-xl font-semibold mb-2">Programme unavailable</h1>
          <p className="mb-6 text-muted-foreground">{error}</p>
          <GhostButton onClick={() => navigate("/")} className="mx-auto max-w-[280px]" data-testid="consumer-error-home">
            Back to home
          </GhostButton>
        </div>
      </div>
    );
  }
  if (!program) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" data-testid="consumer-loading">
        <span className="text-muted-foreground">Loading…</span>
      </div>
    );
  }

  return (
    <ConsumerCtx.Provider value={{ program, attribution }}>
      <div
        className="min-h-[100dvh] bg-background text-foreground"
        style={{ ["--brand" as any]: program.primary_color }}
        data-testid="consumer-flow"
        data-program-slug={program.slug}
        data-program-id={program.program_id}
      >
        <ArrivalRecorder slug={program.slug} programId={program.program_id} />
        <Routes>
          {/* The reskinned funnel starts at the style gallery; the marketing landing
              lives at "/". The legacy S0 splash is retired (kept in-tree, unmounted). */}
          <Route index element={<Navigate to={{ pathname: "style", search: location.search }} replace />} />
          <Route path="style" element={<S1StyleGallery />} />
          <Route path="selfie" element={<S2Selfie />} />
          {/* Lane 2 · repeat buyer fork — reuse the stored avatar or retake. */}
          <Route path="same-face" element={<S2bSameFace />} />
          <Route path="verify" element={<S3VerifyConsent />} />
          <Route path="generating" element={<S4Generating />} />
          <Route path="reveal" element={<S5Reveal />} />
          {/* F9 · sharing is its own screen, reachable from the confirmation. */}
          <Route path="share" element={<S5bShare />} />
          {/* N1 · one screen each. `details` is kept as a redirect so older links
              and any stored resume point land on the first step. */}
          <Route path="details" element={<Navigate to="/create/name" replace />} />
          <Route path="name" element={<NameStep />} />
          <Route path="phone" element={<PhoneStep />} />
          <Route path="dob" element={<DobStep />} />
          <Route path="address" element={<AddressStep />} />
          {/* The fee-only screen is removed; the review screen states the fee. */}
          <Route path="fee" element={<Navigate to="/create/load" replace />} />
          {/* F11 · intended load — universal, its own screen, captured metadata. */}
          <Route path="load" element={<S6bLoad />} />
          {/* N2 · review and payment are separate screens. */}
          <Route path="pay" element={<S7Pay />} />
          <Route path="authorise" element={<S7bAuthorise />} />
          <Route path="confirmed" element={<S8Confirmed />} />
          <Route path="*" element={<Navigate to={{ pathname: "/create/style", search: location.search }} replace />} />
        </Routes>
      </div>
    </ConsumerCtx.Provider>
  );
}
